import { useState, useEffect, useCallback } from 'react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { getV1Progress, retryV1Deployment } from '@/api/schools';
import type { V1DeploymentProgress, V1SetupStatus, V1StepStatus } from '@/api/schools';
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  Loader2,
  Circle,
} from 'lucide-react';

interface V1DeploymentStatusProps {
  schoolCode: string;
  onStatusChange?: (status: V1SetupStatus | null) => void;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

const STATUS_LABELS: Record<V1SetupStatus, string> = {
  not_started: 'Non démarré',
  in_progress: 'En cours',
  completed: 'Terminé',
  failed: 'Échec',
};

const STATUS_COLORS: Record<V1SetupStatus, string> = {
  not_started: 'text-gray-500',
  in_progress: 'text-blue-500',
  completed: 'text-green-500',
  failed: 'text-red-500',
};

function StatusIcon({ status }: { status: V1SetupStatus }) {
  switch (status) {
    case 'completed':
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    case 'failed':
      return <XCircle className="h-5 w-5 text-red-500" />;
    case 'in_progress':
      return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
    default:
      return <Clock className="h-5 w-5 text-gray-500" />;
  }
}

function StepStatusIcon({ status, isCurrentStep }: { status: V1StepStatus; isCurrentStep?: boolean }) {
  if (isCurrentStep && status === 'pending') {
    return <Loader2 className="h-4 w-4 text-blue-500 animate-spin flex-shrink-0" />;
  }
  switch (status) {
    case 'success':
      return <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />;
    case 'failure':
      return <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />;
    case 'pending':
    default:
      return <Circle className="h-4 w-4 text-gray-300 flex-shrink-0" />;
  }
}

export function V1DeploymentStatus({
  schoolCode,
  onStatusChange,
  autoRefresh = true,
  refreshInterval = 2000,
}: V1DeploymentStatusProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<V1DeploymentProgress | null>(null);
  const [restarting, setRestarting] = useState(false);

  const fetchProgress = useCallback(async () => {
    try {
      const result = await getV1Progress(schoolCode);
      setData(result);
      setError(null);
      onStatusChange?.(result.status);
    } catch (err: unknown) {
      const axiosError = err as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        setData({ deployed: false } as V1DeploymentProgress);
        setError(null);
      } else {
        setError((err as Error).message || 'Erreur lors de la récupération du statut');
      }
    } finally {
      setLoading(false);
    }
  }, [schoolCode, onStatusChange]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  useEffect(() => {
    if (!autoRefresh) return;

    const shouldPoll = loading ||
      !data?.deployed ||
      data.status === 'not_started' ||
      data.status === 'in_progress';

    if (!shouldPoll) return;

    const interval = setInterval(fetchProgress, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, loading, data?.deployed, data?.status, fetchProgress, refreshInterval]);

  const handleRetry = async () => {
    setRestarting(true);
    try {
      await retryV1Deployment(schoolCode);
      await fetchProgress();
    } catch (err) {
      setError((err as Error).message || 'Erreur lors du redémarrage');
    } finally {
      setRestarting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-500">Chargement...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!data?.deployed) {
    if (autoRefresh) {
      return (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
          <span className="ml-2 text-gray-500">Initialisation du déploiement V1...</span>
        </div>
      );
    }
    return (
      <div className="text-center p-4 text-gray-500">
        Cette école n'a pas de déploiement V1
      </div>
    );
  }

  const status = data.status;
  const completedSteps = data.completedSteps;
  const totalSteps = data.totalSteps;
  const percentage = data.progressPercentage;

  const statusColors: Record<V1StepStatus, string> = {
    pending: 'bg-gray-50 border-gray-200',
    success: 'bg-green-50 border-green-200',
    failure: 'bg-red-50 border-red-200',
  };

  return (
    <div className="space-y-4">
      {/* Header with status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StatusIcon status={status} />
          <span className={`font-medium ${STATUS_COLORS[status]}`}>
            {STATUS_LABELS[status]}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={fetchProgress} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          {status === 'failed' && (
            <Button variant="outline" size="sm" onClick={handleRetry} disabled={restarting}>
              {restarting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1" />
              )}
              Relancer
            </Button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="space-y-1">
        <div className="flex justify-between text-sm text-gray-500">
          <span>{completedSteps} / {totalSteps} étapes</span>
          <span>{Math.round(percentage)}%</span>
        </div>
        <Progress value={percentage} />
      </div>

      {/* Error message */}
      {data.error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{data.error}</AlertDescription>
        </Alert>
      )}

      {/* Steps list */}
      {data.steps && Object.keys(data.steps).length > 0 && (
        <div className="space-y-2">
          {Object.entries(data.steps).map(([stepId, step], index) => {
            const isCurrentStep = data.currentStep === stepId;
            const bgColor = isCurrentStep && step.status === 'pending'
              ? 'bg-blue-50 border-blue-200'
              : statusColors[step.status];

            return (
              <div
                key={stepId}
                className={`flex items-start gap-3 py-2.5 px-3 rounded-lg border ${bgColor}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-400 w-5">{index + 1}.</span>
                  <StepStatusIcon status={step.status} isCurrentStep={isCurrentStep} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">{step.name}</p>
                    {isCurrentStep && step.status === 'pending' && (
                      <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                        En cours
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
